import {
  appendTimestamp,
  convertAgeToYearByClient,
  convertYearToAgeByClient,
  getClientDisplayNameById,
  getSelectedClient,
  getSelectedClientAppliesToType,
  MONTH_MAP,
  MONTH_NUM_MAP,
  MONTH_LIST,
} from "../../common/shared";
import { SERVER_URL, PRINCE_URL } from "../../config";
import axios from "axios";
import { mapBEGoalTOFE, mapFEGoalToBE } from "./goals";
import { mapBEAccountToFEAccount, mapFEAccountToBEAccount } from "./accounts";
import { mapBEIncomeToFEIncome, mapFEIncomeToBEIncome } from "./income";
import { mapPropertyBEtoFE, mapPropertyFEtoBE } from "./properties";
import {
  mapBEKeyDateTypeToFEKeyDate,
  mapFEKeyDateToBEDate,
} from "../../common/shared";
import {
  mapBELiabilityToFELiability,
  mapFELiabilityToBELiability,
} from "./liabilities";
import { mapFromBEData as mapAllocationBEToFe } from "../admin/allocations";
import {
  mapInsuranceBEtoFE,
  mapInsuranceFEtoBE,
  mapFEInsuranceToBEInsurance,
  mapBEInsurancesPoliciesToFE,
} from "./insurance";
import { mapBEExpenseToFE, mapFEExpenseToBE } from "./expenses";
import { mapFEAssetToBEAsset } from "@instream-solutions/instream-design-system/lib/Api/assets";

export const GOAL_BASED = "GOALS_BASED";
export const SSR_PLAN = "SAFE_SAVINGS_RATE";

export const mapAllocationFEtoBE = (allocationPeriod) => {
  const feModel = allocationPeriod.allocationModel;

  const allocationModel = {
    allocationModelId: feModel.allocationModelId,
    allocationModelName: feModel.allocationModelName,
    grossAverageReturn: feModel.returnRate * 100,
    standardDeviation: feModel.risk * 100,
    annualizedReturn: feModel.annualizedReturn * 100,
    customExpenseRatio: feModel.expenseRatio * 100,
    custom: feModel.customValues,
    customGrossAverageReturn: feModel.customReturnRate * 100,
    customStandardDeviation: feModel.customRisk * 100,
    customAnnualizedReturn: feModel.customAnnualizedReturn * 100,
    createdDate: feModel.createdDate,
    lastUpdateDate: feModel.lastUpdateDate,
    lastUpdatedBy: feModel.lastUpdatedBy,
    defaultForPlan: feModel.defaultForPlan,
    objectScope: feModel.objectScope,
    superAssetClassList: feModel.assets.map((category) => {
      return {
        id: category.assetTypeId,
        name: category.name,
        percentage: category.value * 100,
        assetClassList: category.children.map((asset) => {
          return {
            id: asset.assetId,
            superAssetClassId: asset.superAssetId,
            name: asset.name,
            percentage: asset.value * 100,
            sortOrder: asset.sortOrder,
          };
        }),
        superAssetClassAlignment: category.alignment,
        sortOrder: category.sortOrder,
        assetOrder: category.assetOrder,
      };
    }),
  };

  return {
    ...allocationPeriod,
    date: {
      ...allocationPeriod.date,
      startDateType: mapFEKeyDateToBEDate(
        allocationPeriod.date.startDateType.key
      ),
      endDateType: mapFEKeyDateToBEDate(allocationPeriod.date.endDateType.key),
      startMonth: isNaN(allocationPeriod.date.startMonth)
        ? MONTH_NUM_MAP[allocationPeriod.date.startMonth]
        : allocationPeriod.date.startMonth,
      endMonth: isNaN(allocationPeriod.date.endMonth)
        ? MONTH_NUM_MAP[allocationPeriod.date.endMonth]
        : allocationPeriod.date.endMonth,
    },
    allocationModel: allocationModel,
  };
};

const mapWealthOverTimeToFE = (wealthOverTime) => {
  if (wealthOverTime === null) return [];

  const years = wealthOverTime.yearlyPortfolioAmountPercentileLow.map(
    (p) => p.year
  );

  return years.map((y, index) => {
    return {
      highPresent:
        wealthOverTime.yearlyPortfolioAmountPercentileHigh[index].presentAmount,
      medianPresent:
        wealthOverTime.yearlyPortfolioAmountPercentileMedian[index]
          .presentAmount,
      lowPresent:
        wealthOverTime.yearlyPortfolioAmountPercentileLow[index].presentAmount,
      highFuture:
        wealthOverTime.yearlyPortfolioAmountPercentileHigh[index].futureAmount,
      medianFuture:
        wealthOverTime.yearlyPortfolioAmountPercentileMedian[index]
          .futureAmount,
      lowFuture:
        wealthOverTime.yearlyPortfolioAmountPercentileLow[index].futureAmount,
      year: y,
    };
  });
};

const mapPlanListBEToFE = (listEntry, index) => {
  return {
    id: listEntry.planId,
    isPartialPlan: listEntry.status !== "Completed",
    planId: listEntry.planId,
    editIndex: index,
    planName: listEntry.planName,
    calculationRequired: listEntry.calculationRequired,
    expectedProbability: listEntry.resultPercentileValue,
    safeSavingsRate: listEntry.resultPercentileValue,
    planType: listEntry.planType,
    createdDate: listEntry.createddate
      ? new Date(listEntry.createddate).toLocaleDateString()
      : null,
    lastUpdatedDate: listEntry.lastupdatedate
      ? new Date(listEntry.lastupdatedate).toLocaleDateString()
      : null,
    lastupdatedate: new Date(listEntry.lastupdatedate),
    tracking: {
      enabled: listEntry.tracking.enabled,
      high: listEntry.tracking.maxThreshold,
      low: listEntry.tracking.minThreshold,
      alertEventFrequency: listEntry.tracking.alertEventFrequency,
      alertEventPriority: listEntry.tracking.alertEventPriority,
      startingdate: listEntry.tracking.startingdate,
    },
    sharing: {
      clientPortal: listEntry.clientPortalEnabled,
      isPrimaryPlan: listEntry.primaryPlan,
      cressetEnabled: listEntry.cressetEnabled,
      orionEnabled: listEntry.orionEnabled,
    },
    retirementGoals: listEntry.retirementGoal,
    educationGoals: listEntry.educationGoal,
    legacyGoals: listEntry.legacyGoal,
    lifestyleGoals: listEntry.lifeStyleGoal,
    insuranceGoals: listEntry.insuranceGoal,
    shortfall: {
      year: 2042,
      medianAge: 0,
      medianAmountPresent: 0,
    },
    endingPortfolioValue: listEntry.futureEpvAmount,
    cashFlow: [],
    inFlow: [],
    outFlow: [],
  };
};

export const mapFETaxPeriodstoBE = (taxPeriod) => {
  return {
    TaxPeriodName: taxPeriod.name,
    taxPeriodId: taxPeriod.id,
    taxPeriodType: taxPeriod.taxPeriodType,
    title: taxPeriod.name,
    taxRate: taxPeriod.taxRate * 100,
    taxState: taxPeriod.taxState === "CUSTOM" ? null : taxPeriod.taxState,
    date: {
      startDateType: "REGULAR_DATE",
      endDateType: "REGULAR_DATE",
      relativeStartDateNum: 0,
      relativeEndDateNum: 0,
      startYear: taxPeriod.startYear,
      endYear: taxPeriod.endYear,
      startMonth: MONTH_NUM_MAP["Jan"],
      endMonth: MONTH_NUM_MAP["Dec"],
    },
  };
};

const mapBETaxPeriodstoFE = (taxPeriod, index) => {
  return {
    id: taxPeriod.taxPeriodId,
    taxPeriodType: taxPeriod.taxPeriodType,
    name: taxPeriod.title,
    taxRate: taxPeriod.taxRate / 100,
    taxState: taxPeriod.taxState ? taxPeriod.taxState : "CUSTOM",
    startYear: taxPeriod.date.startYear,
    startAge: convertYearToAgeByClient(
      taxPeriod.date.startYear,
      getSelectedClient({ id: 0 }),
      "Jan"
    ),
    endYear: taxPeriod.date.endYear,
    endAge: convertYearToAgeByClient(
      taxPeriod.date.endYear,
      getSelectedClient({ id: 0 }),
      "Dec"
    ),
    inEdit: false,
    editIndex: index,
  };
};

const mapBEPlanTaxPeriodstoFE = (taxPeriods) => {
  return {
    capitalGainTaxPeriods: taxPeriods.capitalGainsTaxPeriodViews
      ? taxPeriods.capitalGainsTaxPeriodViews.map(mapBETaxPeriodstoFE)
      : [],
    stateTaxPeriods: taxPeriods.stateTaxPeriodViews
      ? taxPeriods.stateTaxPeriodViews.map(mapBETaxPeriodstoFE)
      : [],
    incomeTaxPeriods: taxPeriods.incomeTaxPeriodViews
      ? taxPeriods.incomeTaxPeriodViews.map(mapBETaxPeriodstoFE)
      : [],
    isMarginalTax: taxPeriods.taxGlidePathInclude,
  };
};

const mapPlanDistributionBEtoFE = (distribution) => {
  return {
    id: distribution.distributionId,
    frequency: { key: distribution.amount.frequency, name: "Yearly" },
    appliesTo: {
      id: distribution.appliesTo.appliesToId
        ? distribution.appliesTo.appliesToId
        : 0,
      name: getClientDisplayNameById(distribution.appliesTo.appliesToId),
    },
    amount: distribution.amount.amount,
    inflationRate: distribution.amount.inflationRate,
    startDate: {
      keyDateType: mapBEKeyDateTypeToFEKeyDate(distribution.date.startDateType),
      month: MONTH_MAP[distribution.date.startMonth - 1],
      year: distribution.date.startYear,
      age: 0,
      relativeAge: distribution.date.relativeStartDateNum,
    },
    endDate: {
      keyDateType: mapBEKeyDateTypeToFEKeyDate(distribution.date.endDateType),
      month: MONTH_MAP[distribution.date.endMonth - 1],
      year: distribution.date.endYear,
      age: 0,
      relativeAge: distribution.date.relativeEndDateNum,
    },
    notes: distribution.note,
    objectScope: distribution.scope,
  };
};

export const mapPlanDistributionFEtoBE = (distribution) => {
  return {
    distributionId: distribution.id,
    description: null,
    title: "Distribution",
    appliesTo: {
      appliesToId: distribution.appliesTo.id,
      appliesToType: getSelectedClientAppliesToType(distribution.appliesTo),
    },
    amount: {
      frequency: distribution.frequency.key,
      amount: distribution.amount,
      inflationRate: distribution.inflationRate * 100,
      futureValue: distribution.futureValue,
    },
    date: {
      startDateType: mapFEKeyDateToBEDate(
        distribution.startDate.keyDateType.key
      ),
      endDateType: mapFEKeyDateToBEDate(distribution.endDate.keyDateType.key),
      relativeStartDateNum: distribution.startDate.relativeAge,
      relativeEndDateNum: distribution.endDate.relativeAge,
      startYear: distribution.startDate.year,
      endYear: distribution.endDate.year,
      startMonth: MONTH_NUM_MAP[distribution.startDate.month],
      endMonth: MONTH_NUM_MAP[distribution.endDate.month],
    },
  };
};

const mapPlanRulesBEtoFE = (rules) => {
  return {
    ceilingAmount: rules.ceilingAmount,
    ceilingOn: rules.ceilingOn,
    distributionInflationCapOn: rules.distributionInflationCapOn,
    floorAmount: rules.floorAmount,
    floorOn: rules.floorOn,
    inflationAdjustmentOn: rules.inflationAdjustmentOn,
    maxContributionAmount: rules.maxContributionAmount,
    maxDistributionOn: rules.maxDistributionOn,
    maxInflationAdjustmentPercent: rules.maxInflationAdjustmentPercent / 100,
    maxPercentOfPortfolio: rules.maxPercentOfPortfolio / 100,
    maxPercentOfPortfolioAdjustment:
      rules.maxPercentOfPortfolioAdjustment / 100,
    minDistributionOn: rules.minDistributionOn,
    minPercentOfPortfolio: rules.minPercentOfPortfolio / 100,
    minPercentOfPortfolioAdjustment:
      rules.minPercentOfPortfolioAdjustment / 100,
    minPortfolioReturnPercent: rules.minPortfolioReturnPercent / 100,
    portfolioReturnDistributionFreezeOn:
      rules.portfolioReturnDistributionFreezeOn,
    id: rules.rulesId,
    title: rules.title,
  };
};

export const mapPlanRulesFEtoBE = (rules) => {
  return {
    ceilingAmount: rules.ceilingAmount,
    ceilingOn: rules.ceilingOn,
    distributionInflationCapOn: rules.distributionInflationCapOn,
    floorAmount: rules.floorAmount,
    floorOn: rules.floorOn,
    inflationAdjustmentOn: rules.inflationAdjustmentOn,
    maxContributionAmount: rules.maxContributionAmount,
    maxDistributionOn: rules.maxDistributionOn,
    maxInflationAdjustmentPercent: rules.maxInflationAdjustmentPercent * 100,
    maxPercentOfPortfolio: rules.maxPercentOfPortfolio * 100,
    maxPercentOfPortfolioAdjustment:
      rules.maxPercentOfPortfolioAdjustment * 100,
    minDistributionOn: rules.minDistributionOn,
    minPercentOfPortfolio: rules.minPercentOfPortfolio * 100,
    minPercentOfPortfolioAdjustment:
      rules.minPercentOfPortfolioAdjustment * 100,
    minPortfolioReturnPercent: rules.minPortfolioReturnPercent * 100,
    portfolioReturnDistributionFreezeOn:
      rules.portfolioReturnDistributionFreezeOn,
    rulesId: rules.id,
    title: rules.title,
  };
};

export const mapInflationPeriodsBEtoFE = (inflationPeriods) => {
  return inflationPeriods.map((p) => {
    if (p.date.startMonth === null) p.date.startMonth = 1;
    if (p.date.endMonth === null) p.date.endMonth = 1;

    p.date.startDateType = mapBEKeyDateTypeToFEKeyDate(p.date.startDateType);
    p.date.endDateType = mapBEKeyDateTypeToFEKeyDate(p.date.endDateType);

    return p;
  });
};

export const mapInflationPeriodsFEtoBE = (period) => {
  return {
    title: period.title,
    inflationRate: period.inflationRate,
    standardDeviation: period.standardDeviation,
    inflationPeriodId: period.inflationPeriodId,
    date: {
      ...period.date,
      startDateType: mapFEKeyDateToBEDate(period.date.startDateType),
      endDateType: mapFEKeyDateToBEDate(period.date.endDateType),
    },
  };
};

export const getPlanList = (advisorId, clientId) => {
  console.log("getPlanList");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/list`
    );

    axios.get(targetUrl).then((response) => {
      const payload = response.data.payload.map(mapPlanListBEToFE);
      resolve(payload);
    });
  });
};

export const getPlanDetails = (advisorId, client, planId) => {
  console.log("getPlanDetails");

  return new Promise((resolve, reject) => {
    getPlanOutputSummary(advisorId, client, planId).then((outputData) => {
      getPlanElements(advisorId, client.id, planId).then((elements) => {
        resolve({
          id: planId,
          name: elements.planName,
          clientLifeExpectancyAge: elements.clientLifeExpectancyAge,
          clientRetirementAge: elements.clientRetirementAge,
          coClientLifeExpectancyAge: elements.coClientLifeExpectancyAge,
          coClientRetirementAge: elements.coClientRetirementAge,
          createdDate: elements.createdDate,
          planType: elements.distribution ? SSR_PLAN : GOAL_BASED,
          calculationRequired: outputData.isCalculationRequired,
          customDistributionOrder: elements.customDistributionOrder,
          expectedProbability: outputData.successProbability / 100,
          safeSavingsRate: outputData.safeSavingsRate,
          cumulativeWealth: outputData.cumulativeWealth,
          tracking: outputData.tracking,
          shortfall: {
            ...outputData.shortfall,
            year:
              outputData.shortfall && outputData.shortfall.medianAge
                ? convertAgeToYearByClient(
                    outputData.shortfall.medianAge,
                    client,
                    "Dec"
                  )
                : null,
          },
          wealthOverTime: mapWealthOverTimeToFE(outputData.wealthOvertime),
          elements: elements,
        });
      });
    });
  });
};

export const getPlanOutputSummary = (advisorId, client, planId) => {
  console.log("getPlanOutputSummary");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${client.id}/plan/${planId}/output-summary`
    );

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

const mapAllocationPeriodToFE = (p) => {
  if (p.date.startMonth === null) p.date.startMonth = 1;
  if (p.date.endMonth === null) p.date.endMonth = 1;

  p.date.startDateType = mapBEKeyDateTypeToFEKeyDate(p.date.startDateType);
  p.date.endDateType = mapBEKeyDateTypeToFEKeyDate(p.date.endDateType);

  p.allocationModel = mapAllocationBEToFe(p.allocationModel);
  p.allocationModel.assets = p.allocationModel.assets.map((cat) => {
    cat.value = cat.value /= 100;
    cat.children = cat.children.map((asset) => {
      asset.value /= 100.0;
      return asset;
    });
    return cat;
  });
  return p;
};

export const getPlanElements = (advisorId, clientId, planId) => {
  console.log("getPlanElements");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/element`
    );

    axios.get(targetUrl).then((response) => {
      const elements = response.data.payload;

      const accounts = elements.accounts
        ? elements.accounts.map(mapBEAccountToFEAccount)
        : [];

      resolve({
        planName: elements.planName,
        createdDate: elements.createddate,
        clientRetirementAge: elements.clientRetirementAge,
        clientLifeExpectancyAge: elements.clientLifeExpectancyAge,
        coClientRetirementAge: elements.coClientRetirementAge,
        coClientLifeExpectancyAge: elements.coClientLifeExpectancyAge,
        goals: elements.goals ? elements.goals.map(mapBEGoalTOFE) : [],
        customDistributionOrder: elements.isCustomDistributionOrder,
        accounts: accounts.sort((itemA, itemB) => {
          if (itemA.sortOrder === null || itemB.sortOrder === null) return 0;
          return itemA.sortOrder - itemB.sortOrder;
        }),
        incomes: elements.incomes
          ? elements.incomes.map(mapBEIncomeToFEIncome)
          : [],
        taxPeriods: mapBEPlanTaxPeriodstoFE(elements.taxPeriods),
        marginalTaxBrackets: elements.marginalTaxBracket,
        insurances: elements.insurances
          ? elements.insurances.map(mapInsuranceBEtoFE)
          : [],
        expenses: elements.expenses
          ? elements.expenses.map(mapBEExpenseToFE)
          : [],
        insurancePolicies: elements.insurancePolicies
          ? elements.insurancePolicies.map(mapBEInsurancesPoliciesToFE)
          : [],
        taxDeductions: elements.taxDeductions.taxDeductions,
        yearVrsTaxableIncome: elements.taxDeductions.yearVsTaxableIncome,
      });
    });
  });
};

export const deletePlan = (advisorId, clientId, planId) => {
  console.log("deletePlan");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}`
    );

    axios.delete(targetUrl).then(() => {
      resolve({});
    });
  });
};

export const batchDeletePlan = (advisorId, clientId, planIdList) => {
  console.log("batchDeletePlan");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/batch-delete`
    );

    axios.put(targetUrl, planIdList).then(() => {
      resolve({});
    });
  });
};

export const calculatePlan = (
  advisorId,
  clientId,
  planId,
  expectedProbability
) => {
  console.log("calculatePlan");

  const expectedProbabilityParam =
    expectedProbability != null
      ? `?expectedProbability=${expectedProbability * 100}`
      : "";

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/calculate${expectedProbabilityParam}`
    );

    axios.put(targetUrl).then(() => {
      resolve({});
    });
  });
};

const updatePlanElement = (
  advisorId,
  clientId,
  planId,
  elementType,
  element,
  mapping
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/${elementType}`
    );

    axios.put(targetUrl, element).then((response) => {
      resolve(mapping(response.data.payload));
    });
  });
};

const createPlanElement = (
  advisorId,
  clientId,
  planId,
  elementType,
  element,
  mapping
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/${elementType}`
    );

    element.scopeType = "PLAN";

    axios.post(targetUrl, element).then((response) => {
      resolve(mapping(response.data.payload));
    });
  });
};

const copyPlanElement = (
  advisorId,
  clientId,
  planId,
  elementType,
  element,
  mapping
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/copy-${elementType}`
    );

    axios.post(targetUrl, element).then((response) => {
      resolve(mapping(response.data.payload));
    });
  });
};

const deletePlanElement = (
  advisorId,
  clientId,
  planId,
  elementType,
  elementId
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/${elementType}/${elementId}`
    );

    axios.delete(targetUrl).then(() => {
      resolve({});
    });
  });
};

export const savePlanProperty = (planId, callback) => {
  console.log("savePlanProperty");

  return (advisorId, clientId, property) => {
    let promise = null;

    if (property.id === null) {
      promise = createPlanElement(
        advisorId,
        clientId,
        planId,
        "property",
        mapPropertyFEtoBE(property),
        mapPropertyBEtoFE
      );
    } else {
      promise = updatePlanElement(
        advisorId,
        clientId,
        planId,
        "property",
        mapPropertyFEtoBE(property),
        mapPropertyBEtoFE
      );
    }

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const copyPlanProperty = (planId, callback) => {
  console.log("copyPlanProperty");

  return (advisorId, clientId, property) => {
    let promise = copyPlanElement(
      advisorId,
      clientId,
      planId,
      "property",
      mapPropertyFEtoBE(property),
      mapPropertyBEtoFE
    );

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const deletePlanProperty = (planId, callback) => {
  console.log("deletePlanProperty");

  return (advisorId, clientId, propertyId) => {
    const promise = deletePlanElement(
      advisorId,
      clientId,
      planId,
      "property",
      propertyId
    );
    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const savePlanInsurance = (planId, callback) => {
  console.log("savePlanInsurance");

  return (advisorId, clientId, insurance) => {
    let promise = null;

    if (insurance.id === null) {
      promise = createPlanElement(
        advisorId,
        clientId,
        planId,
        "insurance",
        mapInsuranceFEtoBE(insurance),
        mapInsuranceBEtoFE
      );
    } else {
      promise = updatePlanElement(
        advisorId,
        clientId,
        planId,
        "insurance",
        mapInsuranceFEtoBE(insurance),
        mapInsuranceBEtoFE
      );
    }

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const copyPlanInsurance = (planId, callback) => {
  console.log("copyPlanInsurance");

  return (advisorId, clientId, insurance) => {
    let promise = copyPlanElement(
      advisorId,
      clientId,
      planId,
      "insurance",
      mapInsuranceFEtoBE(insurance),
      mapInsuranceBEtoFE
    );

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const deletePlanInsurance = (planId, callback) => {
  console.log("deletePlanInsurance");

  return (advisorId, clientId, insuranceId) => {
    const promise = deletePlanElement(
      advisorId,
      clientId,
      planId,
      "insurance",
      insuranceId
    );
    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const savePlanLiability = (planId, callback) => {
  console.log("savePlanLiablity");

  return (advisorId, clientId, liability) => {
    let promise = null;

    if (liability.liabilityId === null) {
      promise = createPlanElement(
        advisorId,
        clientId,
        planId,
        "liability",
        mapFELiabilityToBELiability(liability),
        mapBELiabilityToFELiability
      );
    } else {
      promise = updatePlanElement(
        advisorId,
        clientId,
        planId,
        "liability",
        mapFELiabilityToBELiability(liability),
        mapBELiabilityToFELiability
      );
    }
    promise.then(() => {
      if (callback) callback();
    });
    return promise;
  };
};

export const copyPlanLiability = (planId, callback) => {
  console.log("copyPlanLiability");

  return (advisorId, clientId, liability) => {
    let promise = copyPlanElement(
      advisorId,
      clientId,
      planId,
      "liability",
      mapFELiabilityToBELiability(liability),
      mapBELiabilityToFELiability
    );

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const deletePlanLiability = (planId, callback) => {
  console.log("deletePlanLiability");

  return (advisorId, clientId, liabilityId) => {
    const promise = deletePlanElement(
      advisorId,
      clientId,
      planId,
      "liability",
      liabilityId
    );
    promise.then(() => {
      if (callback) callback();
    });
    return promise;
  };
};

export const createPlanTaxPeriod = (planId, callback) => {
  console.log("createPlanPeriod");

  return (advisorId, clientId, planTaxType) => {
    const promise = new Promise((resolve, reject) => {
      const targetUrl = appendTimestamp(
        `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/taxperiod/${planTaxType}`
      );

      axios.post(targetUrl, planTaxType).then((response) => {
        resolve(response.data.payload);
      });
    });

    promise.then(() => {
      if (callback) callback();
    });
    return promise;
  };
};

export const savePlanTaxPeriod = (advisorId, clientId, planId, taxPeriods) => {
  console.log("savePlanPeriod");

  const targetUrl = appendTimestamp(
    `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/taxperiod`
  );
  const payload = taxPeriods.map(mapFETaxPeriodstoBE);
  return axios.post(targetUrl, payload);
};

export const deletePlanTaxPeriod = (
  advisorId,
  clientId,
  planId,
  taxPeriodId
) => {
  console.log("deletePlanTaxperiod");

  return deletePlanElement(
    advisorId,
    clientId,
    planId,
    "taxperiod",
    taxPeriodId
  );
};

export const updatePlanTaxGlidePathFlag = (
  advisorId,
  clientId,
  planId,
  flag
) => {
  return new Promise((resolve, reject) => {
    console.log("updatePlanTaxGlidePathFlag");

    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-glide-path/${flag}`
    );

    axios.put(targetUrl, flag).then(() => {
      resolve({});
    });
  });
};

export const savePlanGoal = (advisorId, clientId, planId, goalList) => {
  console.log("savePlanGoal");

  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/goals`;
  const payload = goalList.map(mapFEGoalToBE);

  return new Promise((resolve, reject) => {
    axios.post(targetUrl, payload).then((response) => {
      const goalList = response.data.payload.map(mapBEGoalTOFE);
      resolve(goalList);
    });
  });
};

export const savePlanAssets = (advisorId, clientId, planId, assetList) => {
  console.log("savePlanAssets");

  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/accounts`;
  const payload = assetList.map(mapFEAccountToBEAccount);

  return new Promise((resolve, reject) => {
    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload.map(mapBEAccountToFEAccount));
    });
  });
};

export const savePlanLifeInsurance = (
  advisorId,
  clientId,
  planId,
  insuranceList
) => {
  console.log("savePlanInsurance");

  const targetUrl = appendTimestamp(
    `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/insurance-policy`
  );

  const payload = insuranceList.map(mapFEInsuranceToBEInsurance(clientId));

  return new Promise((resolve, reject) => {
    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload.map(mapBEInsurancesPoliciesToFE));
    });
  });
};

export const copyPlanGoal = (planId, callback) => {
  console.log("copyPlanGoal");

  return (advisorId, clientId, goal) => {
    let promise = copyPlanElement(
      advisorId,
      clientId,
      planId,
      "goal",
      mapFEGoalToBE(goal),
      mapBEGoalTOFE
    );

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const deletePlanGoal = (advisorId, clientId, planId, goalIdList) => {
  console.log("deletePlanGoal");
  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/goal/batch-delete`;
  return axios.post(targetUrl, goalIdList);
};

export const deletePlanInsurancePolicy = (
  advisorId,
  clientId,
  planId,
  insurancePolicyIdList
) => {
  console.log("deletePlanInsurance");

  const idList = Array.isArray(insurancePolicyIdList)
    ? insurancePolicyIdList
    : [insurancePolicyIdList];

  const targetUrl = appendTimestamp(
    `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/insurance-policy/batch-delete`
  );
  return axios.post(targetUrl, idList);
};

export const savePlanIncome = (advisorId, clientId, planId, incomeList) => {
  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/incomes`;
  const payload = incomeList.map(mapFEIncomeToBEIncome);

  return new Promise((resolve, reject) => {
    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload.map(mapBEIncomeToFEIncome));
    });
  });
};

export const saveExpenseList = (advisorId, clientId, planId, expenseList) => {
  console.log("saveExpenseList");

  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/expenses`;
  const payload = expenseList.map(mapFEExpenseToBE);

  return new Promise((resolve, reject) => {
    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload.map(mapBEExpenseToFE));
    });
  });
};

export const deleteExpenseList = (advisorId, clientId, planId, batchIdList) => {
  console.log("deleteExpenseList");

  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/expense/batch-delete`;
  return axios.post(targetUrl, batchIdList);
};

export const deleteAssetList = (advisorId, clientId, planId, batchIdList) => {
  console.log("deleteAssetList");

  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/account/batch-delete`;
  return axios.post(targetUrl, batchIdList);
};

export const savePlanTaxes = (advisorId, clientId, planId, payload) => {
  console.log("savePlanTaxes");

  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/plan-wizard-taxes`;
  return axios.post(targetUrl, payload);
};

export const copyPlanIncome = (planId, callback) => {
  console.log("copyPlanIncome");

  return (advisorId, clientId, income) => {
    const promise = copyPlanElement(
      advisorId,
      clientId,
      planId,
      "income",
      mapFEIncomeToBEIncome(income),
      mapBEIncomeToFEIncome
    );

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const deletePlanIncome = (advisorId, clientId, planId, incomeIdList) => {
  const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/income/batch-delete`;
  return axios.post(targetUrl, incomeIdList);
};

export const savePlanDistribution = (
  advisorId,
  clientId,
  planId,
  distribution
) => {
  console.log("savePlanDistribution");

  return new Promise((resolve, reject) => {
    let promise = null;

    if (distribution.id === null) {
      promise = createPlanElement(
        advisorId,
        clientId,
        planId,
        "distribution",
        mapPlanDistributionFEtoBE(distribution),
        mapPlanDistributionBEtoFE
      );
    } else {
      promise = updatePlanElement(
        advisorId,
        clientId,
        planId,
        "distribution",
        mapPlanDistributionFEtoBE(distribution),
        mapPlanDistributionBEtoFE
      );
    }

    promise.then((response) => {
      resolve(response);
    });
  });
};

export const savePlanRules = (advisorId, clientId, planId, rules) => {
  console.log("savePlanRules");

  return new Promise((resolve, reject) => {
    let promise = updatePlanElement(
      advisorId,
      clientId,
      planId,
      "plan-rules",
      mapPlanRulesFEtoBE(rules),
      mapPlanRulesBEtoFE
    );

    promise.then((response) => {
      resolve(response);
    });
  });
};

export const savePlanAccount = (planId, callback) => {
  console.log("savePlanAccount");

  return (advisorId, clientId, account) => {
    let promise = null;

    if (account.id === null) {
      promise = createPlanElement(
        advisorId,
        clientId,
        planId,
        "account",
        mapFEAccountToBEAccount(account),
        mapBEAccountToFEAccount
      );
    } else {
      promise = updatePlanElement(
        advisorId,
        clientId,
        planId,
        "account",
        mapFEAccountToBEAccount(account),
        mapBEAccountToFEAccount
      );
    }

    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const deletePlanAccount = (planId, callback) => {
  console.log("deletePlanAccount");

  return (advisorId, clientId, accountId) => {
    const promise = deletePlanElement(
      advisorId,
      clientId,
      planId,
      "account",
      accountId
    );
    promise.then(() => {
      if (callback) callback();
    });

    return promise;
  };
};

export const updatePlanName = (advisorId, clientId, planId, planName) => {
  return new Promise((resolve, reject) => {
    console.log("updatePlanName");

    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/name`
    );

    axios.put(targetUrl, planName).then(() => {
      resolve({});
    });
  });
};

export const copyPlan = (advisorId, clientId, planId, planType) => {
  console.log("copyPlan");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/copy`
    );
    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getPlanSettings = (advisorId, clientId, planId) => {
  console.log("getPlanSettings");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/simulation-settings`
    );

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const savePlanSettings = (advisorId, clientId, planId, settings) => {
  console.log("savePlanSettings");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/simulation-settings`
    );

    axios.put(targetUrl, settings).then(() => {
      resolve({});
    });
  });
};

export const savePlanTracking = (advisorId, clientId, planId, tracking) => {
  console.log("savePlanTracking");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tracking`
    );

    axios.put(targetUrl, tracking).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getStateTaxRate = (advisorId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/meta/advisor/${advisorId}/default-state-taxes`
    );
    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

const mapBEAllocationPeriodToFE = (p) => {
  if (p.date.startMonth === null) p.date.startMonth = 1;
  if (p.date.endMonth === null) p.date.endMonth = 1;

  p.date.startDateType = mapBEKeyDateTypeToFEKeyDate(p.date.startDateType);
  p.date.endDateType = mapBEKeyDateTypeToFEKeyDate(p.date.endDateType);

  p.allocationModel = mapAllocationBEToFe(p.allocationModel);
  p.allocationModel.assets = p.allocationModel.assets.map((cat) => {
    cat.value = cat.value /= 100;
    cat.children = cat.children.map((asset) => {
      asset.value /= 100.0;
      return asset;
    });
    return cat;
  });
  return p;
};

export const getGoalPlanDefaults = (advisorId, clientId) => {
  console.log("getGoalPlanDefaults");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/goals-based-wizard-input`
    );

    axios.get(targetUrl).then((response) => {
      resolve({
        ...response,
        data: {
          ...response.data,
          payload: {
            ...response.data.payload,
            planElementsGoalBasedView: {
              ...response.data.payload.planElementsGoalBasedView,
              allocationPeriods: response.data.payload.planElementsGoalBasedView.allocationPeriods.map(
                mapBEAllocationPeriodToFE
              ),
            },
          },
        },
      });
    });
  });
};

export const getSSRPlanDefaults = (advisorId, clientId) => {
  console.log("getSSRPlanDefaults");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/ssr-wizard-input`
    );

    axios.get(targetUrl).then((response) => {
      resolve({
        ...response,
        data: {
          ...response.data,
          payload: {
            ...response.data.payload,
            planElementsSSRView: {
              ...response.data.payload.planElementsSSRView,
              allocationPeriods: response.data.payload.planElementsSSRView.allocationPeriods.map(
                mapBEAllocationPeriodToFE
              ),
            },
          },
        },
      });
    });
  });
};

export const savePlanAllocationPeriods = (
  advisorId,
  clientId,
  planId,
  allocationPeriods
) => {
  console.log("savePlanAllocationPeriods");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/clients/${clientId}/plan/${planId}/allocations`
    );

    const payload = allocationPeriods.map(mapAllocationFEtoBE);

    axios.put(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const deletePlanAllocationPeriod = (planId, callback) => {
  return (advisorId, clientId, allocationPeriodId) => {
    const promise = deletePlanElement(
      advisorId,
      clientId,
      planId,
      "allocationPeriod",
      allocationPeriodId
    );
    promise.then(() => {
      if (callback) callback();
    });
    return promise;
  };
};

export const createPlan = (advisorId, clientId, planName) => {
  console.log("createPlan");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/new`
    );

    const payload = {
      planId: null,
      planName: planName,
      planSubType: "GOALS_BASED_ALA_STRAIGHT_LINE",
      isMarginalTax: true,
    };

    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const createGoalBasedPlan = (advisorId, clientId, payload) => {
  console.log("createGoalBasedPlan");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/goalsbasedplan`
    );

    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const createSSRPlan = (advisorId, clientId, payload) => {
  console.log("createSSRPlan");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/ssrplan`
    );

    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

const mapInflationPeriodFEtoBE = (p) => {
  return p;
};

export const savePlanInflationPeriods = (
  advisorId,
  clientId,
  planId,
  inflationPeriods
) => {
  console.log("savePlanInflationPeriods");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/clients/${clientId}/plan/${planId}/inflations`
    );

    const payload = inflationPeriods.map(mapInflationPeriodFEtoBE);

    axios.put(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getGlidePathAllocationPeriods = (
  advisorId,
  clientId,
  glidePathId,
  endYear
) => {
  console.log("getGlidePathAllocationPeriods");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/glidepath/${glidePathId}/get-allocation-periods?endYear=${endYear}`
    );

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload.map(mapAllocationPeriodToFE));
    });
  });
};

export const addGoalsToPlan = (advisorId, clientId, planId, goalList) => {
  console.log("addGoalsToPlan");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/goals`
    );

    axios.post(targetUrl, goalList).then((response) => {
      resolve({});
    });
  });
};

export const getOutputDetails = (advisorId, clientId, planId) => {
  console.log("getOutputDetails");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/output-details`
    );

    axios.get(targetUrl).then((response) => {
      console.log(response);

      const payload = response.data.payload;

      resolve({
        ...payload,
        wealthOvertime: mapWealthOverTimeToFE(payload.wealthOvertime),
      });
    });
  });
};

export const getAdvisorReportCustomization = (advisorId) => {
  console.log("getAdvisorReportCustomization");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/settings/report-output-setting-lite`
    );

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getUsedElementsInPlan = (advisorId, clientId, elementId, type) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/used-in-plans?profileElement=${type}&elementId=${elementId}`
    );

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const setPlanCustomSort = (
  advisorId,
  clientId,
  planId,
  isCustomSort
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/distribution-order?customSort=${isCustomSort}`
    );

    axios.put(targetUrl).then(() => {
      resolve({});
    });
  });
};

export const generatePlanReport = (
  advisorId,
  clientId,
  reportOutput,
  theme,
  filenameOnly
) => {
  const getDate = new Date();
  const today = getDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
    day: "numeric",
  });

  const templateHeader = `<!DOCTYPE html><html>
                            <head lang="en">
                              <meta charset="UTF-8" />
                              <link rel="stylesheet" href="${PRINCE_URL}preview.css" />
                              <link rel="stylesheet" href="${PRINCE_URL}preview_theme.css" />
                              <link rel="stylesheet" href="${PRINCE_URL}all.css" />
                              <style>
                              @page {
                                size: A4;
                                margin: 1in 1in 1in 1in;
                                padding: 0;
                                font-family: "Lato", sans-serif;
                                @bottom-left {
                                    content: flow(${PRINCE_URL}/email/engage-full-color.svg);
                                    margin-bottom: 36px;
                                    text-align: left;
                   
                                  background-image: url('${PRINCE_URL}/email/engage-full-color.svg');
                                  background-size: 55px 35px;
                                  background-repeat: no-repeat;
                                  display: inline-block;
                                  width: 15px; 
                                  height: 15px;
                                  content:"";
                                  margin-top: 20px;
                                }
                                  @bottom-center {
                                    content: "Print Date: ${today}";
                                    margin-bottom: 20px;
                                    font-size: 14px;
                                    color: black;
                                    white-space: nowrap;
                                    font-family: "inStreamFont", sans-serif;
                                }
                                @bottom-right {
                                    content: "Page " counter(page) " of " counter(pages);
                                    margin-bottom: 20px;
                                    white-space: nowrap;
                                    text-align: right;
                                    font-size: 14px;
                                    color: black;
                                    font-family: "inStreamFont", sans-serif;
                                }
                              }
                              </style>
                            </head>
                            <body><div class="planOutput ${theme}">`;
  const templateFooter = `</div></body></html>`;
  const template = `${templateHeader}${reportOutput}${templateFooter}`;

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/clients/${clientId}/generatepdf?type=plansummary`
    );

    axios
      .post(targetUrl, template, {
        headers: { "Content-Type": "application/json" },
      })
      .then((response) => {
        const filename = escape(response.data.payload);
        const result = filenameOnly
          ? filename
          : `${SERVER_URL}/advisor/${advisorId}/clients/${clientId}/downloadpdf/${filename}`;
        resolve(result);
      });
  });
};

export const resetPlanTaxes = (advisorId, clientId, planId) => {
  console.log("resetPlanTaxes");

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/reset-taxes`
    );

    axios.post(targetUrl, {}).then((response) => {
      resolve(response.data.status);
    });
  });
};

export const deleteAllRolloverPlanElements = (advisorId, clientId, planId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/roll-over`
    );

    axios.delete(targetUrl).then(() => {
      resolve({});
    });
  });
};

export const updateAllRolloverPlanElements = (advisorId, clientId, planId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/roll-over`
    );

    axios.put(targetUrl).then((response) => {
      resolve({});
    });
  });
};

export const updatePlanStatus = (advisorId, clientId, planId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/status`
    );

    axios.put(targetUrl).then((response) => {
      resolve({});
    });
  });
};

const mapFilingStatusViewBEtoFE = (status, index) => {
  return {
    id: index,
    editIndex: index,
    amount: null,
    inflation: null,
    taxFillingStatusType: {
      name: status.taxFillingStatusType,
      key: status.taxFillingStatusType,
    },
    start: new Date(status.startYear, status.startMonth - 1, 1),
    startYear: status.startYear,
    startMonth: MONTH_LIST[status.startMonth - 1],
    end: new Date(status.endYear, status.endMonth - 1, 1),
    endYear: status.endYear,
    endMonth: MONTH_LIST[status.endMonth - 1],
    isSurvivor: status.isSurvivor,
    inEdit: false,
  };
};

const mapDeductionViewBEtoFE = (deduction, index) => {
  return {
    id: index,
    editIndex: index,
    deductionType: {
      name: deduction.deductionType,
      key: deduction.deductionType,
    },
    amount: deduction.amount,
    inflation: deduction.inflation / 100.0,
    start: new Date(deduction.startYear, deduction.startMonth - 1, 1),
    startYear: deduction.startYear,
    startMonth: MONTH_LIST[deduction.startMonth - 1],
    end: new Date(deduction.endYear, deduction.endMonth - 1, 1),
    endYear: deduction.endYear,
    endMonth: MONTH_LIST[deduction.endMonth - 1],
    isSurvivor: deduction.isSurvivor,
    inEdit: false,
  };
};

const mapTaxCreditViewBEtoFE = (item, index) => {
  return {
    id: index,
    editIndex: index,
    scope: item.scope,
    taxCreditType: {
      name: item.taxCreditType,
      key: item.taxCreditType,
    },
    limitAmount: item.limitAmount,
    creditAmount: item.creditAmount,
    inflation: item.inflation / 100.0,
    inEdit: false,
  };
};

const mapTaxBracketViewBEtoFE = (item, index) => {
  return {
    id: index,
    editIndex: index,
    taxBracketType: {
      name: item.taxBracketType,
      key: item.taxBracketType,
    },
    inflation: item.inflation / 100.0,
    start: new Date(item.startYear, item.startMonth - 1, 1),
    startYear: item.startYear,
    startMonth: MONTH_LIST[item.startMonth - 1],
    end: new Date(item.endYear, item.endMonth - 1, 1),
    endYear: item.endYear,
    endMonth: MONTH_LIST[item.endMonth - 1],
    retirementState: item.retirementState,
    inEdit: false,
  };
};

export const getPlanTaxes = (advisorId, clientId, planId) => {
  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/plan-taxes`
    );

    axios.get(targetUrl).then((response) => {
      const payload = response.data.payload;

      resolve({
        filingStatus: payload.planTaxFillingStatusViews.map(
          mapFilingStatusViewBEtoFE
        ),
        taxDeduction: payload.planTaxDeductionTypeViews.map(
          mapDeductionViewBEtoFE
        ),
        taxBracket: payload.planTaxBracketViews.map(mapTaxBracketViewBEtoFE),
        taxOptions: payload.planTaxOptionViews,
      });
    });
  });
};

export const getPlanTaxCredits = (advisorId, clientId, planId) => {
  console.log("getPlanTaxCredits");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-credits-filing-status`;

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const savePlanTaxCredit = (advisorId, clientId, planId, data) => {
  console.log("savePlanTaxCredit");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-credits`;
    axios.post(targetUrl, data).then(() => {
      resolve({});
    });
  });
};

export const savePlanTaxDeduction = (advisorId, clientId, planId, data) => {
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-deduction-type`;
    axios.put(targetUrl, data).then((response) => {
      resolve({});
    });
  });
};

export const savePlanTaxFilingStatus = (advisorId, clientId, planId, data) => {
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-filing`;
    axios.post(targetUrl, data).then((response) => {
      resolve({});
    });
  });
};

export const savePlanTaxBracket = (advisorId, clientId, planId, data) => {
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-bracket`;
    axios.post(targetUrl, data).then((response) => {
      resolve({});
    });
  });
};

export const savePlanSurvivorTaxDeduction = (
  advisorId,
  clientId,
  planId,
  deduction
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/survivor-tax-deduction-type`;
    axios.put(targetUrl, deduction).then((response) => {
      resolve({});
    });
  });
};

export const generateRothIRAConversionViewReport = (
  advisorId,
  clientId,
  output,
  theme
) => {
  const templateHeader = `<!DOCTYPE html><html><head lang="en"><meta charset="UTF-8" /><link rel="stylesheet" href="${PRINCE_URL}preview.css" /><link rel="stylesheet" href="${PRINCE_URL}preview_theme.css" /><link rel="stylesheet" href="${PRINCE_URL}all.css" /></head><body><div class="planOutput roth-ira-conversion-view-report-page-modal-div ${theme}">`;
  const templateFooter = `</div></body></html>`;
  const template = `${templateHeader}${output}${templateFooter}`;

  return new Promise((resolve, reject) => {
    const targetUrl = appendTimestamp(
      `${SERVER_URL}/advisor/${advisorId}/clients/${clientId}/generatepdf`
    );

    axios
      .post(targetUrl, template, {
        headers: { "Content-Type": "application/json" },
      })
      .then((response) => {
        const filename = escape(response.data.payload);
        resolve({
          fullPath: `${SERVER_URL}/advisor/${advisorId}/clients/${clientId}/downloadpdf/${filename}`,
          fileName: filename,
        });
      });
  });
};

export const sendReportEmail = (advisorId, clientId, data) => {
  console.log("sendEmailRothIRAConversionViewReport");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/clients/${clientId}/email-report-to-advisor`;

    axios.post(targetUrl, data).then((response) => {
      resolve({});
    });
  });
};

export const saveLtcAsExpense = (
  advisorId,
  clientId,
  planId,
  appliesToId,
  appliesToType,
  facilityMonthlyExpense,
  age,
  yearsOfCareRequired
) => {
  const payload = {
    owner: {
      appliesToId: appliesToId,
      appliesToType: appliesToType,
    },
    facilityMothlyExpense: facilityMonthlyExpense,
    age: age,
    yearsOfCareRequired: yearsOfCareRequired,
  };
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/add-ltc-expense`;
    axios.post(targetUrl, payload).then((response) => {
      resolve(response);
    });
  });
};

export const getRothConversionEstimatedReport = (
  advisorId,
  clientId,
  planId,
  data
) => {
  console.log("getRothConversionEstimatedReport");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/roth-conversion-estimated-report`;

    axios.post(targetUrl, data).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const savePlanRothIraConversion = (
  advisorId,
  clientId,
  planId,
  data
) => {
  console.log("savePlanRothIraConversion");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/plan-roth-ira-conversion`;

    axios.post(targetUrl, data).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getRothIRAPlanInputs = (advisorId, clientId, planId) => {
  console.log("getRothIRAPlanInputs");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/plan-roth-Roth-conversion-calc`;

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const calculateRothIRAReport = (
  advisorId,
  clientId,
  planId,
  payload
) => {
  console.log("calculateRothIRAReport");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/roth-conversion-calculator-report`;

    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const cleanUpPlanCreation = (advisorId, clientId, planId) => {
  console.log("cleanUpPlanCreation");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/delete-default-zero-income-expense`;

    axios.delete(targetUrl).then(resolve);
  });
};

export const updateRmdAssetsSelection = (
  advisorId,
  clientId,
  planId,
  accountIdList
) => {
  console.log("updateRmdAssetsSelection");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/update-rmd-selection`;

    axios.post(targetUrl, accountIdList).then(() => resolve({}));
  });
};

export const evaluateFilingStatus = (
  advisorId,
  clientId,
  planId,
  taxFilingStatus
) => {
  console.log("evaluateFilingStatus");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/fillingStatus-evaluation?taxFilingStatus=${taxFilingStatus}`;

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const saveTaxFilingChangesUponDeath = (
  advisorId,
  clientId,
  planId,
  payload
) => {
  console.log("saveTaxFilingChangesUponDeath");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-change-upon-death`;

    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getRothConversionEstimation = (
  advisorId,
  clientId,
  planId,
  payload
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/roth-conversion-estimation`;

    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getPlanRothConversionInput = (advisorId, clientId, planId) => {
  console.log("getPlanRothConversionInput");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/plan-roth-conversion-input`;
    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getDefaultTaxCredits = (advisorId) => {
  console.log("getDefaultTaxCredits");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/settings/tax-credits`;

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const evaluateEarlyDistribution = (advisorId, clientId, planId) => {
  console.log("evaluateEarlyDistribution");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/evaluate-early-distribution`;

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
      /*resolve({
        earlyDistribution: true,
        earlyNoPenaltyAssetDistribution: false,
      });*/
    });
  });
};

export const addEarlyDistributionPenaltyExpense = (
  advisorId,
  clientId,
  planId
) => {
  console.log("addEarlyDistributionPenaltyExpense");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/add-early-distribution-penalty-account`;

    axios.post(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const evaluateSSSurvivorBenefits = (advisorId, clientId, planId) => {
  console.log("evaluateSSSurvivorBenefits");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/evaluate-ss-survivor-benefit-eligibility`;

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const saveSSSurvivorBenefits = (
  advisorId,
  clientId,
  planId,
  survivorBenefits
) => {
  console.log("saveSSSurvivorBenefits");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/create-ss-survivor-benefit`;

    axios.post(targetUrl, survivorBenefits).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const saveTaxOption = (advisorId, clientId, planId, taxOption) => {
  console.log("saveTaxOption");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/tax-option`;

    axios.post(targetUrl, taxOption).then(() => {
      resolve({});
    });
  });
};

export const getSmartTagData = (advisorId, clientId, planId) => {
  console.log("getSmartTagData");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/smart-tags`;

    axios.get(targetUrl).then((response) => {
      resolve(response.data.payload);
    });
  });
};

// !mock endpoint.
export const checkIfPlanHasEarlyDistribution = (
  advisorId,
  clientId,
  planId
) => {
  console.log("planHasEarlyDistribution");

  return new Promise((resolve, reject) => {
    resolve(true);
  });
};

const transformPlanAdjustmentsPayload = (payload, clientId) => {
  return {
    ...payload,
    incomes: payload.incomes.map((item) => {
      return mapFEIncomeToBEIncome(item, clientId);
    }),
    expenses: payload.expenses.map((item) => {
      return mapFEExpenseToBE(item, clientId);
    }),
    insurancePolicies: payload.insurancePolicies.map(
      mapFEInsuranceToBEInsurance(clientId)
    ),
    accounts: payload.accounts.map(mapFEAccountToBEAccount),
  };
};

export const savePlanAdjustmentsToNewPlan = (
  advisorId,
  clientId,
  planId,
  payload
) => {
  console.log("savePlanAdjustmentsToNewPlan");

  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/adjustments-plan`;
    const planData = transformPlanAdjustmentsPayload(payload, clientId);

    axios.post(targetUrl, planData).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const savePlanAdjustmentsToCurrentPlan = (
  advisorId,
  clientId,
  planId,
  payload
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}`;
    const planData = transformPlanAdjustmentsPayload(payload, clientId);

    axios.put(targetUrl, planData).then((response) => {
      resolve(response.data.payload);
    });
  });
};

export const getPlanAdjustmentsTaxInfo = (
  advisorId,
  clientId,
  planId,
  payload
) => {
  return new Promise((resolve, reject) => {
    const targetUrl = `${SERVER_URL}/advisor/${advisorId}/client/${clientId}/plan/${planId}/adjustments/tax-info`;

    axios.post(targetUrl, payload).then((response) => {
      resolve(response.data.payload);
    });
  });
};
